
























import { Component, Vue, Prop } from 'vue-property-decorator';
import {
  MiscellaneousComparisonTitleDetails,
  MonthComparisonTitleDetails,
  SummaryAnalysisOutput,
} from '@/models/swine/analysis.model';
import AnalysisLayout from '@/components/swine/layout/AnalysisLayout.vue';
import BarAndLineChart from '@/components/swine/reports/BarAndLineChart.vue';
import MultiIndustryAppIndexChart from './MultiIndustryAppIndexChart.vue';
import AppIndexAndPrevalenceAnalysisTable from './AppIndexAndPrevalenceAnalysisTable.vue';
import {
  PNEUMONIA_INDEX_INTERPRETATION_LOGO,
  APP_INDEX_AND_PREVALENCE_COLORS,
  DIAMOND_PLOT_LOGO,
  CROSS_PLOT_LOGO,
  ELANCO_LOGO_IMAGE,
  LUNG_LESION_IMAGE,
} from '@/utils/analysisPdfMake';
import { convertDateToDDMMYYYYWithSlashes } from '@/utils/moment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;
import { TDocumentDefinitions } from 'pdfmake/interfaces';

@Component({
  components: {
    AnalysisLayout,
    BarAndLineChart,
    MultiIndustryAppIndexChart,
    AppIndexAndPrevalenceAnalysisTable,
  },
})
export default class AppIndexAndPrevalenceComponent extends Vue {
  @Prop({})
  chartData!: Chart.ChartData;

  @Prop({})
  options!: Chart.ChartOptions;

  @Prop({})
  analysisResponse!: SummaryAnalysisOutput;

  @Prop({})
  comparisonTitle!: MiscellaneousComparisonTitleDetails &
    MonthComparisonTitleDetails;

  @Prop({ required: true })
  isCurrentRoleViewer!: boolean;

  removeNumberOfLungsExamined = false;

  updateRemoveNumberOfLungsExamined(value: boolean) {
    this.removeNumberOfLungsExamined = value;
  }

  generateChartImage() {
    const currentDocument: HTMLCanvasElement | null = document.getElementById(
      'line-chart',
    ) as HTMLCanvasElement;
    if (currentDocument) {
      return currentDocument.toDataURL();
    }
    return '';
  }

  generatePDFBodyTitle() {
    let generatedTitle = `Comparison of ${this.comparisonTitle.selectedOption} by ${this.comparisonTitle.selectedComparison} from ${this.comparisonTitle.startDate} and ${this.comparisonTitle.endDate} `;
    if (this.comparisonTitle.queries) {
      generatedTitle += `between `;
      this.comparisonTitle.queries.forEach((detail, index) => {
        if (index < this.comparisonTitle.queries.length - 1) {
          generatedTitle += `${detail}, `;
        } else {
          generatedTitle += `and ${detail}.`;
        }
      });
    }

    if (this.comparisonTitle.farmUnitCodeName) {
      generatedTitle += `on ${this.comparisonTitle.farmUnitCodeName}.`;
    }

    return generatedTitle;
  }

  generateCountryIndustryAppIndexes() {
    if (!this.analysisResponse.countryQuartiles) {
      return [];
    }
    const countryAppIndexes = [];
    for (const key of Object.keys(this.analysisResponse.countryQuartiles)) {
      countryAppIndexes.push([
        {
          svg: PNEUMONIA_INDEX_INTERPRETATION_LOGO,
          width: 50,
          height: 50,
          border: [false, false, false, false],
          alignment: `center`,
          marginLeft: 20,
        },
        {
          text: `${key} Industry APP Index`,
          fontSize: 16,
          width: 270,
          bold: true,
          height: 40,
          border: [false, false, false, false],
          margin: [12, 15, 50, 15],
        },
        {
          border: [false, false, false, false],
          table: {
            widths: [80, 80, 80, 80],
            heights: [20, 20, 20, 20],
            body: [
              [
                {
                  text: this.analysisResponse.countryQuartiles[key]
                    .firstQuartile,
                  fontSize: 14,
                  alignment: `right`,
                  border: [false, false, false, false],
                },
                {
                  text: this.analysisResponse.countryQuartiles[key]
                    .secondQuartile,
                  fontSize: 14,
                  alignment: `right`,
                  border: [false, false, false, false],
                },
                {
                  text: this.analysisResponse.countryQuartiles[key]
                    .thirdQuartile,
                  fontSize: 14,
                  alignment: `right`,
                  border: [false, false, false, false],
                },
                {
                  text: '',
                  border: [false, false, false, false],
                },
              ],
              [
                {
                  text: '',
                  fillColor: APP_INDEX_AND_PREVALENCE_COLORS.GREEN.BACKGROUND,
                  color: APP_INDEX_AND_PREVALENCE_COLORS.GREEN.TEXT,
                },
                {
                  text: '',
                  fillColor: APP_INDEX_AND_PREVALENCE_COLORS.YELLOW.BACKGROUND,
                  color: APP_INDEX_AND_PREVALENCE_COLORS.YELLOW.TEXT,
                },
                {
                  text: '',
                  fillColor: APP_INDEX_AND_PREVALENCE_COLORS.ORANGE.BACKGROUND,
                },
                {
                  text: '',
                  fillColor: APP_INDEX_AND_PREVALENCE_COLORS.RED.BACKGROUND,
                },
              ],
            ],
          },
        },
      ]);
    }
    return countryAppIndexes;
  }

  generatePDFTableHeader() {
    return [
      {
        text: '',
        fillColor: APP_INDEX_AND_PREVALENCE_COLORS.LIGHT_GREY.BACKGROUND,
        color: APP_INDEX_AND_PREVALENCE_COLORS.LIGHT_GREY.TEXT,
        fontSize: 9,
        border: [false, false, false, true],
        bold: true,
        alignment: `center`,
        marginTop: 15,
      },
      {
        text: 'No. of Lungs Examined',
        fillColor: APP_INDEX_AND_PREVALENCE_COLORS.DARK_GREY.BACKGROUND,
        color: APP_INDEX_AND_PREVALENCE_COLORS.DARK_GREY.TEXT,
        fontSize: 9,
        border: [false, false, false, true],
        bold: true,
        alignment: `center`,
        marginTop: 15,
      },
      {
        text: 'APP Index',
        fillColor: APP_INDEX_AND_PREVALENCE_COLORS.LIGHT_GREY.BACKGROUND,
        color: APP_INDEX_AND_PREVALENCE_COLORS.LIGHT_GREY.TEXT,
        fontSize: 9,
        border: [false, false, false, true],
        bold: true,
        alignment: `center`,
        marginTop: 15,
      },
      {
        fillColor: APP_INDEX_AND_PREVALENCE_COLORS.PURPLE.BACKGROUND,
        color: APP_INDEX_AND_PREVALENCE_COLORS.PURPLE.TEXT,
        border: [false, false, false, true],
        marginLeft: 27,
        alignment: `center`,
        table: {
          body: [
            [
              {
                svg: DIAMOND_PLOT_LOGO,
                alignment: `center`,
                border: [false, false, false, false],
                fillColor: APP_INDEX_AND_PREVALENCE_COLORS.PURPLE.BACKGROUND,
                color: APP_INDEX_AND_PREVALENCE_COLORS.PURPLE.TEXT,
                fontSize: 9,
                bold: true,
              },
            ],
            [
              {
                alignment: `center`,
                text: 'APP Pleurisy Prevalence (%)',
                border: [false, false, false, false],
                fillColor: APP_INDEX_AND_PREVALENCE_COLORS.PURPLE.BACKGROUND,
                color: APP_INDEX_AND_PREVALENCE_COLORS.PURPLE.TEXT,
                fontSize: 9,
                bold: true,
              },
            ],
          ],
        },
      },
      {
        fillColor: APP_INDEX_AND_PREVALENCE_COLORS.WHITE.BACKGROUND,
        color: APP_INDEX_AND_PREVALENCE_COLORS.WHITE.TEXT,
        border: [false, false, false, true],
        marginLeft: 5,
        alignment: `center`,
        table: {
          body: [
            [
              {
                svg: CROSS_PLOT_LOGO,
                alignment: `center`,
                border: [false, false, false, false],
                fillColor: APP_INDEX_AND_PREVALENCE_COLORS.WHITE.BACKGROUND,
                color: APP_INDEX_AND_PREVALENCE_COLORS.WHITE.TEXT,
                fontSize: 9,
                bold: true,
              },
            ],
            [
              {
                text: 'Pleurisy Prevalence (%)',
                fillColor: APP_INDEX_AND_PREVALENCE_COLORS.WHITE.BACKGROUND,
                color: APP_INDEX_AND_PREVALENCE_COLORS.WHITE.TEXT,
                fontSize: 9,
                border: [false, false, false, false],
                bold: true,
                alignment: `center`,
              },
            ],
          ],
        },
      },
    ];
  }

  generatePDFTableContent() {
    if (this.analysisResponse) {
      const tableData = [];
      if (this.analysisResponse.farmUnits) {
        tableData.push(...this.analysisResponse.farmUnits);
      }
      if (this.analysisResponse.industryAverages) {
        tableData.push(...this.analysisResponse.industryAverages);
      }
      if (this.analysisResponse.individualReports) {
        tableData.push(...this.analysisResponse.individualReports);
      }

      const pdfTableDetails = tableData.map((data) => {
        if (data.appIndex) {
          return [
            {
              text: data.identifier,
              fillColor: APP_INDEX_AND_PREVALENCE_COLORS.LIGHT_GREY.BACKGROUND,
              color: APP_INDEX_AND_PREVALENCE_COLORS.LIGHT_GREY.TEXT,
              fontSize: 10,
              border: [false, false, false, true],
              bold: true,
              margin: [3, 3, 0, 3],
            },
            {
              text: data.numberOfLungsExaminedForPleuritis,
              fillColor: APP_INDEX_AND_PREVALENCE_COLORS.DARK_GREY.BACKGROUND,
              color: APP_INDEX_AND_PREVALENCE_COLORS.DARK_GREY.TEXT,
              fontSize: 10,
              border: [false, false, false, true],
              bold: true,
              alignment: `center`,
              margin: [0, 3, 0, 3],
            },
            {
              text: data.appIndex.appIndex,
              fillColor: data.appIndex.appIndexBackgroundColor,
              color:
                data.appIndex.appIndexBackgroundColor !== '#FF4D4D'
                  ? data.appIndex.appIndexColor
                  : '#ffffff',
              fontSize: 10,
              border: [false, false, false, true],
              bold: true,
              alignment: `center`,
              margin: [0, 3, 0, 3],
            },
            {
              text: data.appPleurisyPrevalence,
              fillColor: APP_INDEX_AND_PREVALENCE_COLORS.PURPLE.BACKGROUND,
              color: APP_INDEX_AND_PREVALENCE_COLORS.PURPLE.TEXT,
              fontSize: 10,
              border: [false, false, false, true],
              bold: true,
              alignment: `center`,
              margin: [0, 3, 0, 3],
            },
            {
              text: data.pleurisyPrevalence,
              fillColor: APP_INDEX_AND_PREVALENCE_COLORS.WHITE.BACKGROUND,
              color: APP_INDEX_AND_PREVALENCE_COLORS.WHITE.TEXT,
              fontSize: 10,
              border: [false, false, false, true],
              bold: true,
              alignment: `center`,
              margin: [0, 3, 0, 3],
            },
          ];
        }
      });
      return pdfTableDetails;
    }
  }

  generatePdfTable() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pdfTable: any = [this.generatePDFTableHeader()];
    const details = this.generatePDFTableContent();

    if (details) {
      details.forEach((item) => {
        if (item) {
          pdfTable.push(item);
        }
      });
    }
    return pdfTable;
  }

  generatePDF() {
    if (!this.analysisResponse) {
      return;
    }
    try {
      const imgElancoLogo = ELANCO_LOGO_IMAGE;
      const imgLungLesion = LUNG_LESION_IMAGE;
      const headerTitle = 'Lung Lesion Scoring Analysis';
      pdfMake.tableLayouts = {
        headerLayout: {
          hLineColor: function () {
            return '#dbdbdb';
          },
          vLineColor: function () {
            return '';
          },
          paddingLeft: function () {
            return 0;
          },
          paddingRight: function () {
            return 0;
          },
          paddingTop: function () {
            return 0;
          },
          paddingBottom: function () {
            return 0;
          },
        },
        tableLayout: {
          hLineColor: function () {
            return '#dbdbdb';
          },
          vLineColor: function () {
            return '';
          },
        },
      };

      const docDefinition: TDocumentDefinitions = {
        pageOrientation: 'landscape',
        content: [
          {
            table: {
              widths: ['auto', 'auto', '*'],
              body: [
                [
                  {
                    svg: imgElancoLogo,
                    width: 100,
                    margin: [0, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    svg: imgLungLesion,
                    width: 50,
                    margin: [120, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    text: headerTitle,
                    color: '#333',
                    bold: true,
                    fontSize: 20,
                    margin: [17, 17, 0, 17],
                    border: [false, false, false, true],
                  },
                ],
              ],
            },
            layout: 'headerLayout',
          },
          {
            text: this.generatePDFBodyTitle(),
            bold: true,
            fontSize: 14,
            margin: [0, 30, 0, 30],
          },
          {
            width: 782,
            pageBreak: 'after',
            alignment: 'center',
            table: {
              body: [...this.generateCountryIndustryAppIndexes()],
            },
          },
          {
            table: {
              widths: ['auto', 'auto', '*'],
              body: [
                [
                  {
                    svg: imgElancoLogo,
                    width: 100,
                    margin: [0, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    svg: imgLungLesion,
                    width: 50,
                    margin: [120, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    text: headerTitle,
                    color: '#333',
                    bold: true,
                    fontSize: 20,
                    margin: [17, 17, 0, 17],
                    border: [false, false, false, true],
                  },
                ],
              ],
            },
            layout: 'headerLayout',
          },
          {
            text: this.generatePDFBodyTitle(),
            bold: true,
            fontSize: 14,
            margin: [0, 30, 0, 30],
          },
          {
            image: this.generateChartImage(),
            width: 741,
          },
          {
            text: '',
            pageBreak: 'after',
          },
          {
            table: {
              widths: ['auto', 'auto', '*'],
              body: [
                [
                  {
                    svg: imgElancoLogo,
                    width: 100,
                    margin: [0, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    svg: imgLungLesion,
                    width: 50,
                    margin: [120, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    text: headerTitle,
                    color: '#333',
                    bold: true,
                    fontSize: 20,
                    margin: [17, 17, 0, 17],
                    border: [false, false, false, true],
                  },
                ],
              ],
            },
            layout: 'headerLayout',
          },
          {
            text: this.generatePDFBodyTitle(),
            bold: true,
            fontSize: 14,
            margin: [0, 30, 0, 30],
          },
          {
            table: {
              widths: [112, 130, 180, 180, '*'],
              body: this.generatePdfTable(),
            },
            layout: 'tableLayout',
          },
        ],
        footer: {
          columns: [
            {
              text: `Exported on ${convertDateToDDMMYYYYWithSlashes(
                new Date(),
              )}`,
              fontSize: 10,
              color: '#8e8e8e',
              margin: [30, 0, 0, 0],
            },
          ],
        },
      };
      pdfMake
        .createPdf(docDefinition)
        .download('lung-lesion-scoring-analysis.pdf');
    } catch (error) {
      console.error(error);
    }
  }
}
